import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MasheryToken } from '../../shared/models/mashery-token.model';
import { environment } from '../../../environments/environment';
import { getOccAuthTokenAPI } from '../../shared/constants/end-point-constants';
import { captchaMasheryTokenKey, captchaOccTokenKey, jwtTokenKey, userJwtTokenKey } from '../../shared/constants/storage-key-constants';
import { JwtToken } from '../../shared/models/jwt-token.model';
import { OccToken } from '../../shared/models/occ-token.model';
import { ApiConfigService } from '../api-config/api-config.service';
import { HttpHandlerService } from '../http-handler/http-handler.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LoggerService } from '../logger/logger.service';
import { SessionStorageService } from '../session-storage/session-storage.service';
import { AccessType } from '../../shared/enum/access-type.enum';

@Injectable({
  providedIn: 'root'
})
export class OccTokenHandlerService {

  constructor(
    private localStorage: LocalStorageService,
    private sessionStorage: SessionStorageService,
    private httpHandlerService: HttpHandlerService,
    private apiConfigService: ApiConfigService,
    private loggerService: LoggerService
  ) { }

  /**
   *
   *
   * @name getJwtToken
   * @description Helper function to get JWT Token from session
   * @param isOwnerFlow Boolean to denote user or customer flow
   * @return
   * @memberof OccTokenHandlerService
   */
  getJwtToken(isOwnerFlow: boolean): JwtToken {
    let jwtToken: JwtToken = null;
    if (isOwnerFlow) {
      jwtToken = this.localStorage.getItem(jwtTokenKey);
    } else {
      jwtToken = this.sessionStorage.getItem(userJwtTokenKey);
    }
    return (jwtToken) ? <JwtToken>(jwtToken) : <JwtToken>({});
  }

  /**
   *
   *
   * @name setJwtToken
   * @description Helper function to set JWT Token in session
   * @param token Jwttoken
   * @param isOwnerFlow Boolean to denote user or customer flow
   * @memberof OccTokenHandlerService
   */
  setJwtToken(token: JwtToken, isOwnerFlow: boolean) {
    if (isOwnerFlow) {
      this.localStorage.setItem(jwtTokenKey, token);
    } else {
      this.sessionStorage.setItem(userJwtTokenKey, token);
    }
  }

  /**
   *
   *
   * @name fetchAndUpdateJwtToken
   * @description Function to fetch and update accesstoken in session
   * @param refreshToken Refresh token
   * @param isOwnerFlow Denote if owner or customer/user flow
   * @memberof OccTokenHandlerService
   */
  fetchAndUpdateJwtToken(refreshToken: string, isOwnerFlow: boolean): Observable<boolean> {
    const tokenLoaded$: Observable<boolean> = new Observable((observer) => {
      this.fetchAuthUsingRefreshToken(refreshToken)
        .subscribe((occToken: OccToken) => {
          const jwtToken: JwtToken = this.getJwtToken(isOwnerFlow);
          jwtToken.creatorShopOCCBearerToken = occToken.accessToken;
          jwtToken.refreshToken = occToken.refreshToken;
          jwtToken.exp = occToken.expiresIn;
          this.setJwtToken(jwtToken, isOwnerFlow);
          observer.next(true);
          observer.complete();
        }, error => {
          observer.error();
          observer.complete();
        });
    });
    return tokenLoaded$;
  }

  /**
   *
   *
   * @name fetchAuthUsingRefreshToken
   * @description Function to fetch access token using refresh token
   * @param refreshToken Refresh token
   * @return
   * @memberof OccTokenHandlerService
   */
  fetchAuthUsingRefreshToken(refreshToken: string): Observable<OccToken> {
    const authToken$: Observable<OccToken> = new Observable<OccToken>(observer => {
      const api = this.apiConfigService.getAPIPath(getOccAuthTokenAPI);
      let urlSearchParams: HttpParams = new HttpParams();
      urlSearchParams = urlSearchParams.set('client_id', environment.idpClientId);
      urlSearchParams = urlSearchParams.set('client_secret', environment.authClientSecret);
      urlSearchParams = urlSearchParams.set('grant_type', 'refresh_token');
      urlSearchParams = urlSearchParams.set('refresh_token', refreshToken);

      this.httpHandlerService.callServerPOST(api, urlSearchParams)
        .subscribe(response => {
          observer.next(new OccToken(response));
          observer.complete();
        }, err => {
          observer.error(err);
          observer.complete();
        });
    });
    return authToken$;
  }

  /**
   *
   *
   * @name logHttpError
   * @description Function to log http error
   * @param err Error
   * @memberof OccTokenHandlerService
   */
  logHttpError(err: string) {
    this.loggerService.logError('error logged from interceptor: ' + err);
  }

  checkAuthorizationToken(authorization: string): string {
    const captchaOccToken = <OccToken>this.sessionStorage.getItem(captchaOccTokenKey);
    const captchaMasheryToken = <MasheryToken>this.sessionStorage.getItem(captchaMasheryTokenKey);
    const ownerJwtToken: JwtToken = this.getJwtToken(true);
    const userJwtToken: JwtToken = this.getJwtToken(false);
    let accessType = '';
    if (captchaOccToken && captchaOccToken.accessToken === authorization) {
      accessType = AccessType.CAPTCHA_OCC;
    } else if (captchaMasheryToken && captchaMasheryToken.accessToken === authorization) {
      accessType = AccessType.CAPTCHA_MASHERY;
    } else if (ownerJwtToken && ownerJwtToken.creatorShopOCCBearerToken === authorization) {
      accessType = AccessType.OWNER_OCC;
    } else if (ownerJwtToken && ownerJwtToken.masheryToken === authorization) {
      accessType = AccessType.OWNER_MASHERY;
    } else if (userJwtToken && userJwtToken.creatorShopOCCBearerToken === authorization) {
      accessType = AccessType.USER_OCC;
    } else if (userJwtToken && userJwtToken.masheryToken === authorization) {
      accessType = AccessType.USER_MASHERY;
    } else {
      accessType = AccessType.NONE;
    }
    return accessType;
  }
}
